export const REPORTING_AGENT_VERSION = 1 as const

export type ReportingAgentVersion = typeof REPORTING_AGENT_VERSION
export type ReportingId = string
export type ReportingCell = string | boolean | null

export interface ReportingColumn {
  key: string
  label: string
  physical_type: string
  semantic_type: string
  nullable: boolean
  currency_field: string | null
  unit: string | null
}

export interface ReportingDataset {
  version: ReportingAgentVersion
  dataset_id: ReportingId
  columns: ReportingColumn[]
  rows: Array<Record<string, ReportingCell>>
  row_count: number
  complete: boolean
  generated_at: string
}

export type ReportingChartKind =
  | 'kpi'
  | 'line'
  | 'horizontal_bar'
  | 'grouped_bar'
  | 'stacked_bar'
  | 'diverging_bar'
  | 'pie'
  | 'table_only'

export interface ReportingFieldReference {
  field: string
  format?: string | null
}

export type ReportingChartEncoding =
  | { value: ReportingFieldReference; series?: never }
  | {
      x: ReportingFieldReference
      y: ReportingFieldReference
      series?: ReportingFieldReference | null
    }
  | {
      category: ReportingFieldReference
      value: ReportingFieldReference
    }
  | { series?: never }

export interface ReportingChartSpec {
  version: ReportingAgentVersion
  dataset_id: ReportingId
  kind: ReportingChartKind
  title: string
  sort: Array<{ field: string; direction: 'ascending' | 'descending' }>
  table: {
    dataset_id: ReportingId
    columns: string[]
    formatting: Record<string, string>
    default_expanded: boolean
  }
  encoding: ReportingChartEncoding
  top_n_other?: { limit: number; label: 'Other' } | null
}

export interface DraftSubmissionRequest {
  version: ReportingAgentVersion
  question: string
  chart_context?: ReportingChartQuestionContextRef
}

export interface ReportingChartContextDisplay {
  title: string
  context_label: string
}

export type ReportingChartQuestionContextSource =
  | {
      kind: 'overview'
      analysis_context_token: string
      chart:
        | 'total_company_spend'
        | 'spend_by_accounting_category'
        | 'spend_by_department'
        | 'spend_by_vendor'
      since: string
      until: string
      currency: string
    }
  | {
      kind: 'live_report'
      analysis_context_token: string
      report_id: ReportingId
    }
  | {
      kind: 'artifact'
      origin_conversation_id: ReportingId
      artifact_id: ReportingId
    }

export interface ReportingChartQuestionContextRef {
  version: ReportingAgentVersion
  source: ReportingChartQuestionContextSource
  display: ReportingChartContextDisplay
}

export type ReportingRunStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'

export interface DraftSubmissionProjection {
  version: ReportingAgentVersion
  conversation_id: ReportingId
  message_id: ReportingId
  run_id: ReportingId
  status: 'queued' | 'running'
  events_url: string
}

export interface ReportingMessage {
  version: ReportingAgentVersion
  id: ReportingId
  conversation_id: ReportingId
  role: 'user' | 'assistant'
  content: string
  run_id: ReportingId | null
  chart_context: ReportingResolvedChartContext | null
  created_at: string
}

export interface ReportingResolvedChartContext {
  version: ReportingAgentVersion
  chart_context_id: ReportingId
  source_kind: 'overview' | 'live_report' | 'artifact'
  title: string
  context_label: string
  anchor_ref: { anchor_id: ReportingId; anchor_revision: number }
  generated_at: string
  presentation: ReportingResolvedChartPresentation
}

export interface ReportingOverviewSpendPoint {
  label: string
  spend_cents: number
  key: string | null
  bucket: string | null
  series_key: string | null
  series_label: string | null
  metadata: Record<string, string | null>
}

export interface ReportingOverviewSpendChart {
  chart:
    | 'total_company_spend'
    | 'spend_by_accounting_category'
    | 'spend_by_department'
    | 'spend_by_vendor'
  since: string
  until: string
  currency: string
  points: ReportingOverviewSpendPoint[]
  total_spend_cents: number
}

export type ReportingResolvedChartPresentation =
  | { kind: 'overview'; spend_chart: ReportingOverviewSpendChart }
  | {
      kind: 'reporting_artifact'
      chart_spec: ReportingChartSpec | null
      dataset: ReportingDataset
    }

export interface ReportingMessagePage {
  version: ReportingAgentVersion
  items: ReportingMessage[]
  next_cursor: string | null
}

export interface MessageSubmissionProjection {
  version: ReportingAgentVersion
  message_id: ReportingId
  run_id: ReportingId
  status: 'queued' | 'running'
  events_url: string
}

export interface ReportingRunProjection {
  version: ReportingAgentVersion
  id: ReportingId
  conversation_id: ReportingId
  trigger_message_id: ReportingId
  status: ReportingRunStatus
  artifact_id: ReportingId | null
  created_at: string
  updated_at: string
}

export interface DraftDisplayProjection {
  version: ReportingAgentVersion
  artifact_id: ReportingId
  /** Candidate binding required to pin an agent artifact as a live report. */
  live_report_candidate_id?: ReportingId | null
  conversation_id: ReportingId
  title: string
  generated_at: string
  result_expires_at: string | null
  chart_spec: ReportingChartSpec | null
  dataset: ReportingDataset
}

export interface SavedReportSummary {
  version: ReportingAgentVersion
  id: ReportingId
  title: string
  artifact_id: ReportingId
  created_at: string
  updated_at: string
}

export interface SavedReportDetail {
  version: ReportingAgentVersion
  id: ReportingId
  title: string
  artifact_id: ReportingId
  chart_spec: ReportingChartSpec | null
  dataset: ReportingDataset
  created_at: string
  updated_at: string
}

export interface SavedReportPage {
  version: ReportingAgentVersion
  items: SavedReportSummary[]
  next_cursor: string | null
}

export interface ReportingRunEvent {
  version: ReportingAgentVersion
  type:
    | 'run.status'
    | 'assistant.text.delta'
    | 'report.progress'
    | 'report.result.ready'
    | 'run.completed'
    | 'run.failed'
    | 'run.cancelled'
  run_id: ReportingId
  sequence: number
  terminal: boolean
  payload:
    | { status: 'queued' | 'running' }
    | { delta: string }
    | {
        stage:
          | 'interpreting_question'
          | 'building_query'
          | 'running_query'
          | 'preparing_visualization'
      }
    | { artifact_id: ReportingId }
    | Record<string, never>
    | {
        code: 'service_unavailable'
        retryable: true
        message: 'The report could not be completed.'
      }
    | {
        code:
          | 'unsupported_data'
          | 'unsupported_chart'
          | 'clarification_required'
        retryable: false
        message: string
      }
    | {
        code:
          | 'query_too_expensive'
          | 'result_too_large'
          | 'query_timeout'
          | 'data_unavailable'
          | 'chart_spec_invalid'
        retryable: boolean
        message: string
      }
    | { reason: 'user_requested' }
}

export interface ReportingStreamCursor {
  sequence: number
  reconnect: true
}
